import { calculateIkrProgress } from './utils';

export const UNASSIGNED_ID = '__unassigned__';

const QUARTER_CODES = ['Q1', 'Q2', 'Q3', 'Q4'];

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
};

const getUserId = (user) => user?.uid || user?.id || null;

/** FY26-27 owner threads carry `quarters`; FY25-26 IKRs carry `quarterlyActuals`. */
const readQuarters = (ikr) => {
  const isThread = Array.isArray(ikr?.quarters);
  return QUARTER_CODES.map((code, i) => {
    if (isThread) {
      const q = ikr.quarters[i] || {};
      return { q: code, text: q.text || '', target: toNumberOrNull(q.target), actual: toNumberOrNull(q.actual) };
    }
    const actuals = Array.isArray(ikr?.quarterlyActuals) ? ikr.quarterlyActuals : [];
    return { q: code, text: '', target: null, actual: toNumberOrNull(actuals[i]) };
  });
};

const normalizeIkr = (ikr, path) => {
  const isThread = Array.isArray(ikr?.quarters);
  const quarters = readQuarters(ikr);

  return {
    id: ikr.id,
    ownerId: ikr.ownerId || null,
    text: (isThread ? ikr.yearlyText : ikr.text) || quarters.find(q => q.text)?.text || '',
    target: toNumberOrNull(isThread ? ikr.yearlyTarget : ikr.kpiTarget),
    unit: ikr.kpiUnit || '',
    calculationMethod: ikr.calculationMethod || 'sum',
    quarters,
    progress: calculateIkrProgress(ikr),
    path,
  };
};

/**
 * Walk a set of objectives and yield every individual KR together with the
 * objective / KR / team-KR it hangs off, for either FY data shape.
 *
 * FY26-27: objective → keyResults → teamKRs → individualKRs
 * FY25-26: objective → keyResults (these _are_ the team KRs) → individualKRs
 */
const forEachIkr = (okrs, variant, visit) => {
  (okrs || []).forEach(objective => {
    (objective.keyResults || []).forEach(kr => {
      const teamKRs = variant === 'FY2627'
        ? (kr.teamKRs || [])
        : [{ id: kr.id, text: kr.text, targetKPI: kr.kpiTarget, unit: kr.kpiUnit, individualKRs: kr.individualKRs }];

      teamKRs.forEach(tkr => {
        (tkr.individualKRs || []).forEach(ikr => visit({ objective, kr, tkr, ikr }));
      });
    });
  });
};

const average = (nums) => (nums.length === 0 ? 0 : nums.reduce((s, n) => s + n, 0) / nums.length);

/**
 * People with work come first, then the Unassigned bucket, then the rest of the
 * roster. Unassigned outranks the idle roster because it holds real orphaned
 * IKRs someone needs to find, whereas an idle person is rarely the search target.
 */
const personRank = (person) => (person.isUnassigned ? 1 : (person.hasIkrs ? 0 : 2));

/**
 * Build a per-person view of the OKR tree:
 *   person → objectives → key results → team key results → individual key results
 *
 * Only branches that actually contain one of the person's IKRs are kept, so the
 * result is exactly "what this person owns". Purely derived / read-only.
 *
 * @param {Array}  okrs     objectives the viewer is allowed to see
 * @param {Array}  users    user documents
 * @param {string} variant  'FY2627' | 'FY2526'
 * @returns {Array} people, sorted by name, each with { id, name, ... , objectives }
 */
export const buildPersonOkrIndex = (okrs, users, variant = 'FY2627') => {
  const usersById = new Map((users || []).map(u => [getUserId(u), u]));
  /** personId → { person, objectivesById: Map } */
  const people = new Map();

  const ensurePerson = (personId) => {
    if (!people.has(personId)) {
      const user = usersById.get(personId);
      people.set(personId, {
        id: personId,
        name: personId === UNASSIGNED_ID ? 'Unassigned' : (user?.name || user?.email || 'Unknown user'),
        email: user?.email || '',
        role: user?.role || '',
        isUnassigned: personId === UNASSIGNED_ID,
        objectivesById: new Map(),
      });
    }
    return people.get(personId);
  };

  // Everyone on the roster is searchable, even with nothing assigned — "no IKR
  // assigned yet" is a valid answer, not a missing person. Without this seed a
  // person only exists if the walk below happens to find an IKR they own.
  (users || []).forEach(user => {
    const id = getUserId(user);
    if (id && id !== UNASSIGNED_ID) ensurePerson(id);
  });

  forEachIkr(okrs, variant, ({ objective, kr, tkr, ikr }) => {
    const personId = ikr.ownerId || UNASSIGNED_ID;
    const entry = ensurePerson(personId);

    if (!entry.objectivesById.has(objective.id)) {
      entry.objectivesById.set(objective.id, {
        id: objective.id,
        code: objective.code || '',
        objective: objective.objective || '',
        krsById: new Map(),
      });
    }
    const objNode = entry.objectivesById.get(objective.id);

    // FY25-26 has no KR layer above the team KR — collapse it away.
    const krKey = variant === 'FY2627' ? kr.id : '__flat__';
    if (!objNode.krsById.has(krKey)) {
      objNode.krsById.set(krKey, {
        id: krKey,
        code: variant === 'FY2627' ? (kr.code || '') : '',
        text: variant === 'FY2627' ? (kr.text || '') : '',
        isFlat: variant !== 'FY2627',
        teamKRsById: new Map(),
      });
    }
    const krNode = objNode.krsById.get(krKey);

    if (!krNode.teamKRsById.has(tkr.id)) {
      krNode.teamKRsById.set(tkr.id, {
        id: tkr.id,
        text: tkr.text || '',
        targetKPI: tkr.targetKPI ?? null,
        unit: tkr.unit || '',
        ikrs: [],
      });
    }
    krNode.teamKRsById.get(tkr.id).ikrs.push(normalizeIkr(ikr, {
      objectiveId: objective.id,
      krId: kr.id,
      tkrId: tkr.id,
      ikrId: ikr.id,
    }));
  });

  return Array.from(people.values())
    .map(entry => {
      const objectives = Array.from(entry.objectivesById.values()).map(obj => {
        const krs = Array.from(obj.krsById.values()).map(kr => {
          const teamKRs = Array.from(kr.teamKRsById.values()).map(tkr => ({
            ...tkr,
            progress: average(tkr.ikrs.map(i => i.progress)),
          }));
          return { id: kr.id, code: kr.code, text: kr.text, isFlat: kr.isFlat, teamKRs, progress: average(teamKRs.flatMap(t => t.ikrs).map(i => i.progress)) };
        }).sort((a, b) => (a.code || '').localeCompare(b.code || ''));

        const allIkrs = krs.flatMap(k => k.teamKRs.flatMap(t => t.ikrs));
        return { id: obj.id, code: obj.code, objective: obj.objective, krs, ikrCount: allIkrs.length, progress: average(allIkrs.map(i => i.progress)) };
      }).sort((a, b) => (a.code || '').localeCompare(b.code || ''));

      const allIkrs = objectives.flatMap(o => o.krs.flatMap(k => k.teamKRs.flatMap(t => t.ikrs)));
      return {
        id: entry.id,
        name: entry.name,
        email: entry.email,
        role: entry.role,
        isUnassigned: entry.isUnassigned,
        objectives,
        ikrCount: allIkrs.length,
        hasIkrs: allIkrs.length > 0,
        progress: average(allIkrs.map(i => i.progress)),
      };
    })
    .sort((a, b) => personRank(a) - personRank(b) || a.name.localeCompare(b.name));
};

/**
 * Rewrite one FY26-27 IKR with an edited quarter value, migrating a legacy
 * (pre-owner-thread) IKR to the thread shape the same way TeamKRRow does.
 */
const patchThreadIkr = (ikr, quarterIndex, field, value) => {
  const isThread = Array.isArray(ikr.quarters);
  const quarters = readQuarters(ikr);
  quarters[quarterIndex] = { ...quarters[quarterIndex], [field]: toNumberOrNull(value) };

  if (isThread) return { ...ikr, quarters };

  const { text, kpiTarget, quarterlyActuals, ...rest } = ikr;
  return {
    ...rest,
    yearlyText: ikr.yearlyText || text || '',
    yearlyTarget: toNumberOrNull(ikr.yearlyTarget ?? kpiTarget),
    kpiUnit: ikr.kpiUnit || '',
    calculationMethod: ikr.calculationMethod || 'sum',
    quarters,
  };
};

/** FY25-26 keeps a flat `quarterlyActuals` array; only the actual is per-quarter. */
const patchLegacyIkr = (ikr, quarterIndex, field, value) => {
  if (field !== 'actual') return ikr;
  const actuals = [...(Array.isArray(ikr.quarterlyActuals) ? ikr.quarterlyActuals : [])];
  while (actuals.length < 4) actuals.push(null);
  actuals[quarterIndex] = toNumberOrNull(value);
  return { ...ikr, quarterlyActuals: actuals.slice(0, 4) };
};

/**
 * Produce the objective's new `keyResults` array with a single quarter field
 * changed on a single IKR, addressed by the `path` carried on the indexed IKR.
 *
 * @returns {Array|null} new keyResults, or null when the path no longer resolves
 */
export const buildKeyResultsWithQuarterEdit = (objective, path, quarterIndex, field, value, variant = 'FY2627') => {
  if (!objective || !path) return null;
  let touched = false;

  const patchIkrs = (ikrs) => (ikrs || []).map(ikr => {
    if (ikr.id !== path.ikrId) return ikr;
    touched = true;
    return variant === 'FY2627'
      ? patchThreadIkr(ikr, quarterIndex, field, value)
      : patchLegacyIkr(ikr, quarterIndex, field, value);
  });

  const keyResults = (objective.keyResults || []).map(kr => {
    if (kr.id !== path.krId) return kr;
    if (variant !== 'FY2627') return { ...kr, individualKRs: patchIkrs(kr.individualKRs) };
    return {
      ...kr,
      teamKRs: (kr.teamKRs || []).map(tkr => (
        tkr.id === path.tkrId ? { ...tkr, individualKRs: patchIkrs(tkr.individualKRs) } : tkr
      )),
    };
  });

  return touched ? keyResults : null;
};

/**
 * Locate one IKR through `path` and report it with its siblings, reading the
 * unpruned objective document. The person index prunes every branch that is not
 * the selected person's, so sibling owners are only visible from here.
 */
const findIkrWithSiblings = (objective, path, variant) => {
  const kr = (objective.keyResults || []).find(k => k.id === path.krId);
  if (!kr) return null;

  // FY25-26 has no team-KR layer; forEachIkr fabricates tkr.id === kr.id there,
  // so path.tkrId is meaningless and kr.individualKRs is the list.
  const list = variant === 'FY2627'
    ? ((kr.teamKRs || []).find(t => t.id === path.tkrId)?.individualKRs || null)
    : (kr.individualKRs || null);
  if (!list) return null;

  const target = list.find(i => i.id === path.ikrId);
  if (!target) return null;

  return {
    target,
    krCode: variant === 'FY2627' ? (kr.code || null) : null,
    siblingOwnerIds: list.filter(i => i.id !== path.ikrId).map(i => i.ownerId || null),
  };
};

/**
 * Produce the objective's new `keyResults` array with one IKR's owner changed,
 * addressed by the `path` carried on the indexed IKR.
 *
 * Two things are deliberately preserved. The IKR's `id`, because it is a foreign
 * key: evaluation documents live at users/{uid}/evaluations*}/{period}/ikrs/{ikrId}
 * and the Excel export joins on it, so regenerating the owner-derived
 * `ikr_owner_<uid>` id would orphan real data (the stale uid in it is cosmetic).
 * And the IKR's shape, because owner is orthogonal to shape — unlike
 * patchThreadIkr this never migrates a legacy flat IKR to the thread shape,
 * keeping a shape change out of the one operation that also writes two user docs.
 *
 * Returns a result object rather than a bare array because the caller needs the
 * previous owner (to revoke access), the KR code (the accessibleKRs key) and a
 * refusal reason to show the admin — none of which fit in an array.
 *
 * @param {Object}  objective   objective document as loaded from Firestore
 * @param {Object}  path        { objectiveId, krId, tkrId, ikrId }
 * @param {?string} newOwnerId  target user id; null / UNASSIGNED_ID to unassign
 * @param {string}  variant     'FY2627' | 'FY2526'
 * @param {Object}  [options]   { newOwnerTeamId } — FY25-26 only, mirrors IndividualKrRow
 * @returns {null
 *   | { ok: false, reason: 'unchanged'|'duplicate-owner', previousOwnerId: ?string }
 *   | { ok: true, keyResults: Array, previousOwnerId: ?string, krCode: ?string }}
 *   null when the path no longer resolves.
 */
export const buildKeyResultsWithOwnerChange = (objective, path, newOwnerId, variant = 'FY2627', options = {}) => {
  if (!objective || !path) return null;

  // null, never undefined: handleUpdateOkr strips undefined only at the top level
  // of the update object, so an undefined nested in keyResults still throws.
  const nextOwnerId = (!newOwnerId || newOwnerId === UNASSIGNED_ID) ? null : newOwnerId;

  const found = findIkrWithSiblings(objective, path, variant);
  if (!found) return null;

  const previousOwnerId = found.target.ownerId || null;
  if (previousOwnerId === nextOwnerId) return { ok: false, reason: 'unchanged', previousOwnerId };

  // FY26-27 only: the owner-thread migration dedupes by ownerId, so two threads
  // sharing an owner inside one team KR would later be merged silently. Several
  // flat rows per owner is normal in FY25-26 (that is what the importer wrote).
  if (variant === 'FY2627' && nextOwnerId && found.siblingOwnerIds.includes(nextOwnerId)) {
    return { ok: false, reason: 'duplicate-owner', previousOwnerId };
  }

  const patchIkrs = (ikrs) => (ikrs || []).map(ikr => {
    if (ikr.id !== path.ikrId) return ikr;
    const next = { ...ikr, ownerId: nextOwnerId };
    // FY25-26 carries the owner's team on the IKR (see IndividualKrRow). Copy the
    // raw value — real data has padded ids like " 3 " and every reader trims.
    if (variant !== 'FY2627' && nextOwnerId && 'newOwnerTeamId' in options) {
      next.teamId = options.newOwnerTeamId ?? null;
    }
    return next;
  });

  const keyResults = (objective.keyResults || []).map(kr => {
    if (kr.id !== path.krId) return kr;
    if (variant !== 'FY2627') return { ...kr, individualKRs: patchIkrs(kr.individualKRs) };
    return {
      ...kr,
      teamKRs: (kr.teamKRs || []).map(tkr => (
        tkr.id === path.tkrId ? { ...tkr, individualKRs: patchIkrs(tkr.individualKRs) } : tkr
      )),
    };
  });

  return { ok: true, keyResults, previousOwnerId, krCode: found.krCode };
};

export const getPersonInitials = (name) => (name || '?')
  .split(' ')
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part[0].toUpperCase())
  .join('');
